/**
 * Project Files Modals
 * Contains all modal components for the Project Files section
 */

import { useState } from 'react';
import { Modal, Form, Button, Row, Col, Badge } from 'react-bootstrap';
import Select from 'react-select';

/**
 * UploadFileModal - Modal for uploading new files
 * 
 * Form fields:
 * - File (file upload with dropzone)
 * - File Name (text input)
 * - Category (select dropdown)
 * - Description (textarea - optional)
 */
export const UploadFileModal = ({
    show,
    onHide,
    uploadFile,
    setUploadFile,
    fileName,
    setFileName,
    fileCategory,
    setFileCategory,
    fileDescription,
    setFileDescription,
    categoryOptions,
    onSubmit
}) => {
    return (
        <Modal show={show} onHide={onHide} backdrop="static" keyboard={false} centered size="lg">
            <Modal.Header closeButton>
                <Modal.Title className='display-1'>Upload File</Modal.Title>
            </Modal.Header>
            <Modal.Body>
                <Form>
                    <Row className='gy-3'>
                        <Col sm={12}>
                            <Form.Group>
                                <Form.Label>File <span className='text-danger'>*</span></Form.Label>
                                <div className="file-upload-dropzone text-center p-4">
                                    <input
                                        type="file"
                                        id="file-upload"
                                        className="d-none"
                                        onChange={(e) => {
                                            const file = e.target.files[0];
                                            setUploadFile(file);
                                            setFileName(file?.name || '');
                                        }}
                                    />
                                    <label htmlFor="file-upload" className="file-upload-label">
                                        <div className="upload-icon mb-3">
                                            <i className="fas fa-cloud-upload-alt"></i>
                                        </div>
                                        <div className="upload-text fs-14 mb-2">
                                            <strong>Click to upload</strong> or drag and drop
                                        </div>
                                        <div className="upload-hint fs-12">
                                            PDF, DOC, ZIP, Images (max. 50MB)
                                        </div>
                                    </label>
                                    {uploadFile && (
                                        <div className="uploaded-file-info d-flex align-items-center gap-2 p-3 mt-3">
                                            <i className="fas fa-file me-2"></i>
                                            <span>{uploadFile.name}</span>
                                            <Button
                                                variant="link"
                                                size="sm"
                                                className="text-danger ms-auto"
                                                onClick={() => setUploadFile(null)}
                                            >
                                                <i className="fas fa-times"></i>
                                            </Button>
                                        </div>
                                    )}
                                </div>
                            </Form.Group>
                        </Col>

                        <Col sm={6}>
                            <Form.Group>
                                <Form.Label>File Name <span className='text-danger'>*</span></Form.Label>
                                <Form.Control
                                    type="text"
                                    placeholder="Enter file name"
                                    value={fileName}
                                    onChange={(e) => setFileName(e.target.value)}
                                />
                            </Form.Group>
                        </Col>

                        <Col sm={6}>
                            <Form.Group>
                                <Form.Label>Category <span className='text-danger'>*</span></Form.Label>
                                <Select
                                    options={categoryOptions}
                                    value={fileCategory}
                                    onChange={setFileCategory}
                                    placeholder="Select category"
                                    className="react-select-container"
                                    classNamePrefix="react-select"
                                    isClearable
                                />
                            </Form.Group>
                        </Col>

                        <Col sm={12}>
                            <Form.Group>
                                <Form.Label>Description <span className="text-muted">(Optional)</span></Form.Label>
                                <Form.Control
                                    as="textarea"
                                    rows={3}
                                    placeholder="Enter file description"
                                    value={fileDescription}
                                    onChange={(e) => setFileDescription(e.target.value)}
                                />
                            </Form.Group>
                        </Col>
                    </Row>
                </Form>
            </Modal.Body>
            <Modal.Footer className='d-flex justify-content-between'>
                <Button variant="link" className='text-danger text-decoration-none' onClick={onHide}>
                    Cancel
                </Button>
                <Button variant="primary" onClick={onSubmit} disabled={!uploadFile}>
                    <i className="fas fa-upload me-2"></i>Upload File
                </Button>
            </Modal.Footer>
        </Modal>
    );
};

/**
 * FilePreviewModal - Modal for previewing file details
 * 
 * Displays:
 * - File preview placeholder
 * - File details (size, uploaded by, category, upload date, description)
 * - Action buttons (rename, download, delete)
 */
export const FilePreviewModal = ({
    show,
    onHide,
    selectedFile,
    categoryOptions,
    getFileIcon,
    getCategoryColor,
    onFileAction
}) => {
    if (!selectedFile) return null;

    const fileIcon = getFileIcon(selectedFile.type);

    return (
        <Modal show={show} onHide={onHide} backdrop="static" keyboard={false} centered >
            <Modal.Header closeButton>
                <Modal.Title className='display-1'>{selectedFile.name}</Modal.Title>
            </Modal.Header>
            <Modal.Body className='p-0'>

                {/* File Details Section */}
                <div className="p-4">
                    <h6 className="fs-16 fw-bold mb-3">File Information</h6>
                    <Row className='g-4'>
                        <Col md={6}>
                            <div className="file-detail-item">
                                <div className="fs-12 text-muted mb-1">File Size</div>
                                <div className="fs-14 fw-semibold">{selectedFile.size}</div>
                            </div>
                        </Col>
                        <Col md={6}>
                            <div className="file-detail-item">
                                <div className="fs-12 text-muted mb-1">Uploaded By</div>
                                <div className="fs-14 fw-semibold">{selectedFile.uploadedBy}</div>
                            </div>
                        </Col>
                        <Col md={6}>
                            <div className="file-detail-item">
                                <div className="fs-12 text-muted mb-1">Category</div>
                                <div className="fs-14 fw-semibold">{selectedFile.category}</div>
                            </div>
                        </Col>
                        <Col md={6}>
                            <div className="file-detail-item">
                                <div className="fs-12 text-muted mb-1">Upload Date</div>
                                <div className="fs-14 fw-semibold">{selectedFile.uploadedDate}</div>
                            </div>
                        </Col>
                        <Col md={12}>
                            <div className="file-detail-item">
                                <div className="fs-14 fw-semibold">Description</div>
                                <p className="fs-14 mb-0">{selectedFile.description}</p>
                            </div>
                        </Col>
                    </Row>
                </div>
            </Modal.Body>
            <Modal.Footer className='d-flex justify-content-between'>
                <Button variant="link" className='text-danger text-decoration-none' onClick={onHide}>
                    Close
                </Button>
                <Button variant="outline-primary" onClick={() => onFileAction('download', selectedFile)}>
                    <i className="fas fa-download me-2"></i>Download
                </Button>
            </Modal.Footer>
        </Modal>
    );
};

/**
 * RenameFileModal - Modal for renaming a file
 * 
 * Displays:
 * - Current file name (non-editable)
 * - New name input field
 * - Validation for empty names and duplicates
 */
export const RenameFileModal = ({
    show,
    onHide,
    file,
    allFiles,
    onRename
}) => {
    const [newName, setNewName] = useState('');
    const [error, setError] = useState('');

    // Reset state when modal opens
    const handleShow = () => {
        if (file) {
            // Get filename without extension
            const nameWithoutExt = file.name.substring(0, file.name.lastIndexOf('.')) || file.name;
            setNewName(nameWithoutExt);
            setError('');
        }
    };

    // Validate and submit
    const handleSubmit = () => {
        // Validation: Cannot be empty
        if (!newName.trim()) {
            setError('File name cannot be empty');
            return;
        }

        // Get file extension
        const extension = file.name.includes('.') ? file.name.substring(file.name.lastIndexOf('.')) : '';
        const newFullName = newName.trim() + extension;

        // Validation: Cannot be the same as current name
        if (newFullName === file.name) {
            setError('New name must be different from current name');
            return;
        }

        // Validation: Cannot have duplicate names in the same folder
        const isDuplicate = allFiles.some(f => 
            f.id !== file.id && 
            f.name.toLowerCase() === newFullName.toLowerCase() &&
            f.folder === file.folder // Assuming same folder check
        );

        if (isDuplicate) {
            setError('A file with this name already exists in this folder');
            return;
        }

        // Success - call parent handler
        onRename(file.id, newFullName);
        onHide();
    };

    const handleClose = () => {
        setError('');
        onHide();
    };

    if (!file) return null;

    // Get file extension for display
    const extension = file.name.includes('.') ? file.name.substring(file.name.lastIndexOf('.')) : '';

    return (
        <Modal 
            show={show} 
            onHide={handleClose} 
            onShow={handleShow}
            backdrop="static" 
            keyboard={false} 
            centered
        >
            <Modal.Header closeButton>
                <Modal.Title className='display-1'>Rename File</Modal.Title>
            </Modal.Header>
            <Modal.Body>
                <Form>
                    <Row className='gy-3'>
                        <Col sm={12}>
                            <Form.Group>
                                <Form.Label>Current Name</Form.Label>
                                <Form.Control
                                    type="text"
                                    value={file.name}
                                    disabled
                                    className="bg-light"
                                />
                            </Form.Group>
                        </Col>

                        <Col sm={12}>
                            <Form.Group>
                                <Form.Label>New Name <span className='text-danger'>*</span></Form.Label>
                                <div className="d-flex align-items-center gap-2">
                                    <Form.Control
                                        type="text"
                                        placeholder="Enter new file name"
                                        value={newName}
                                        onChange={(e) => {
                                            setNewName(e.target.value);
                                            setError('');
                                        }}
                                        isInvalid={!!error}
                                        autoFocus
                                    />
                                    {extension && (
                                        <span className="text-muted fs-14 text-nowrap">{extension}</span>
                                    )}
                                </div>
                                {error && (
                                    <Form.Control.Feedback type="invalid" className="d-block">
                                        {error}
                                    </Form.Control.Feedback>
                                )}
                                <Form.Text className="text-muted">
                                    File extension will be preserved automatically
                                </Form.Text>
                            </Form.Group>
                        </Col>
                    </Row>
                </Form>
            </Modal.Body>
            <Modal.Footer className='d-flex justify-content-between'>
                <Button variant="link" className='text-danger text-decoration-none' onClick={handleClose}>
                    Cancel
                </Button>
                <Button variant="primary" onClick={handleSubmit}>
                    <i className="fas fa-save me-2"></i>Save Changes
                </Button>
            </Modal.Footer>
        </Modal>
    );
};

/**
 * MoveFileModal - Modal for moving files to a different folder
 * 
 * Displays:
 * - List of files to be moved
 * - Folder tree/list for selection
 * - Current folder highlighted
 * - Validation for same folder and duplicates
 */
export const MoveFileModal = ({
    show,
    onHide,
    selectedFiles,
    allFiles,
    folders,
    currentFolder,
    onMove
}) => {
    const [selectedFolderId, setSelectedFolderId] = useState(null);
    const [error, setError] = useState('');

    // Reset state when modal opens
    const handleShow = () => {
        setSelectedFolderId(currentFolder?.id || null);
        setError('');
    };

    // Validate and submit
    const handleSubmit = () => {
        // Validation: Must select a folder
        if (!selectedFolderId) {
            setError('Please select a destination folder');
            return;
        }

        // Validation: Cannot move to the same folder
        if (selectedFolderId === currentFolder?.id) {
            setError('Cannot move files to the same folder');
            return;
        }

        // Validation: Check for duplicate file names in target folder
        const targetFolderFiles = allFiles.filter(f => f.folderId === selectedFolderId);
        const duplicates = selectedFiles.filter(selectedFile =>
            targetFolderFiles.some(targetFile => 
                targetFile.name.toLowerCase() === selectedFile.name.toLowerCase()
            )
        );

        if (duplicates.length > 0) {
            const duplicateNames = duplicates.map(f => f.name).join(', ');
            setError(`The following files already exist in the target folder: ${duplicateNames}`);
            return;
        }

        // Success - call parent handler
        onMove(selectedFiles.map(f => f.id), selectedFolderId);
        onHide();
    };

    const handleClose = () => {
        setError('');
        onHide();
    };

    if (!selectedFiles || selectedFiles.length === 0) return null;

    return (
        <Modal 
            show={show} 
            onHide={handleClose}
            onShow={handleShow}
            backdrop="static" 
            keyboard={false} 
            size="lg"
            centered
        >
            <Modal.Header closeButton>
                <Modal.Title className='display-1'>
                    Move {selectedFiles.length === 1 ? 'File' : `${selectedFiles.length} Files`}
                </Modal.Title>
            </Modal.Header>
            <Modal.Body>
                <Form>
                    <Row className='gy-3'>
                        {/* Files to be moved */}
                        <Col sm={12}>
                            <Form.Group>
                                <Form.Label>Files to Move</Form.Label>
                                <div className="border rounded-2 bg-light p-2" 
                                     style={{ maxHeight: '150px', overflowY: 'auto' }}>
                                    {selectedFiles.map((file, index) => (
                                        <div 
                                            key={file.id} 
                                            className={`d-flex align-items-center gap-2 px-2 py-2 rounded-2 ${index > 0 ? 'mt-1' : ''}`}
                                            style={{ 
                                                backgroundColor: '#ffffff',
                                                border: '1px solid #e9ecef'
                                            }}
                                        >
                                            <i className="fas fa-file-alt text-muted" style={{ fontSize: '14px', width: '16px' }}></i>
                                            <span className="flex-grow-1 text-truncate fw-medium" style={{ fontSize: '13px' }}>
                                                {file.name}
                                            </span>
                                            <span className="text-muted" style={{ fontSize: '12px', whiteSpace: 'nowrap' }}>
                                                {file.size}
                                            </span>
                                        </div>
                                    ))}
                                </div>
                            </Form.Group>
                        </Col>

                        {/* Folder selection */}
                        <Col sm={12}>
                            <Form.Group>
                                <Form.Label>
                                    Destination Folder <span className='text-danger'>*</span>
                                </Form.Label>
                                <div className="border rounded-2 bg-white p-2" 
                                     style={{ maxHeight: '240px', overflowY: 'auto' }}>
                                    {folders && folders.length > 0 ? (
                                        folders.map((folder) => {
                                            const isSelected = selectedFolderId === folder.id;
                                            const isCurrent = folder.id === currentFolder?.id;
                                            
                                            return (
                                                <div
                                                    key={folder.id}
                                                    className="move-folder-item rounded-2 px-3 py-2 mb-1"
                                                    onClick={() => {
                                                        setSelectedFolderId(folder.id);
                                                        setError('');
                                                    }}
                                                    style={{ 
                                                        cursor: 'pointer',
                                                        transition: 'all 0.15s ease',
                                                        border: `1px solid ${isSelected ? 'var(--primary-color)' : isCurrent ? 'var(--success-color)' : '#e9ecef'}`,
                                                        backgroundColor: isSelected ? 'var(--primary-light)' : isCurrent ? '#e8f5e9' : '#f8f9fa'
                                                    }}
                                                >
                                                    <div className="d-flex align-items-center gap-2">
                                                        <i className="fas fa-folder" 
                                                           style={{ 
                                                               color: isSelected ? 'var(--primary-color)' : isCurrent ? 'var(--success-color)' : '#6c757d',
                                                               fontSize: '16px',
                                                               width: '20px'
                                                           }}></i>
                                                        <div className="flex-grow-1 min-w-0">
                                                            <div className="fw-semibold" style={{ fontSize: '13px' }}>
                                                                {folder.name}
                                                            </div>
                                                            {folder.path && (
                                                                <div className="text-muted text-truncate" style={{ fontSize: '11px', marginTop: '1px' }}>
                                                                    {folder.path}
                                                                </div>
                                                            )}
                                                        </div>
                                                        {isCurrent && (
                                                            <span className="move-folder-badge move-folder-badge-current">
                                                                <i className="fas fa-circle" style={{ fontSize: '6px', marginRight: '4px' }}></i>
                                                                Current
                                                            </span>
                                                        )}
                                                        {isSelected && !isCurrent && (
                                                            <span className="move-folder-badge move-folder-badge-selected">
                                                                <i className="fas fa-check" style={{ fontSize: '9px', marginRight: '4px' }}></i>
                                                                Selected
                                                            </span>
                                                        )}
                                                    </div>
                                                </div>
                                            );
                                        })
                                    ) : (
                                        <div className="text-center py-4">
                                            <i className="fas fa-folder-open text-muted mb-2" style={{ fontSize: '32px', opacity: 0.3 }}></i>
                                            <p className="text-muted mb-0" style={{ fontSize: '13px' }}>No folders available</p>
                                        </div>
                                    )}
                                </div>
                                {error && (
                                    <div className="alert alert-danger d-flex align-items-center mt-3 mb-0 py-2 px-3" 
                                         style={{ fontSize: '13px', border: '1px solid #f5c2c7', background: '#f8d7da' }}>
                                        <i className="fas fa-exclamation-triangle me-2"></i>
                                        <span>{error}</span>
                                    </div>
                                )}
                                <Form.Text className="text-muted">
                                    Click on a folder to select it as the destination for moving your file(s)
                                </Form.Text>
                            </Form.Group>
                        </Col>
                    </Row>
                </Form>
            </Modal.Body>
            <Modal.Footer className='d-flex justify-content-between'>
                <Button variant="link" className='text-danger text-decoration-none' onClick={handleClose}>
                    Cancel
                </Button>
                <Button variant="primary" onClick={handleSubmit}>
                    <i className="fas fa-arrows-alt me-2"></i>Move File{selectedFiles.length > 1 ? 's' : ''}
                </Button>
            </Modal.Footer>
        </Modal>
    );
};
